import dayjs from "dayjs";
import { throttle } from "lodash";
import { FC, useCallback, useEffect, useRef } from "react";
import { useEventEnd } from "../../hooks/useEventEnd";

export type GetScrollValueReturn = {
  clientHeight: number;
  innerHeight: number;
  scrollY: number;
};

export const getScrollValues = (): GetScrollValueReturn => ({
  clientHeight:
    typeof window !== "undefined" ? window.document.body.clientHeight : 0,
  innerHeight: typeof window !== "undefined" ? window.innerHeight : 0,
  scrollY: typeof window !== "undefined" ? window.scrollY : 0,
});

export interface ScrollMeasureProps {
  initialOffset?: number;
  onScroll?: (
    clientHeight: number,
    innterHeight: number,
    scrollY: number,
    scrollDiff: number,
    timestamp: number
  ) => void;
  onScrollEnd?: (
    clientHeight: number,
    innterHeight: number,
    scrollY: number,
    scrollDiff: number,
    timestamp: number
  ) => void;
  handleScrollInterval?: number;
  handleScrollEndInterval?: number;
}

/**
 * NOTE:
 *  - 命名ちょっと微妙な気もしてる
 *  - useScrollEffectより出来が良いのでこちらに移行しても良い気がしているが、useSWRInfiniteWithScrollerの実装と要相談か
 *  - コンポーネントの形にしておき、refと組み合わせたonReachBottom関数などを作成し、ページネーション用の関数を渡すだけでページネーションが実装できたら便利そう
 *
 * @param onScroll スクロールしたときに呼び出される関数
 * @param onScrollEnd スクロールが終了したときに呼び出される関数
 * @param handleScrollInterval onScroll関数を発火させる間隔
 * @param handleScrollEndInterval 最後のスクロールから何ミリ秒後にonScrollEnd関数を発火させるか
 */
export const ScrollMeasure: FC<ScrollMeasureProps> = ({
  onScroll,
  onScrollEnd,
  handleScrollInterval = 100,
  handleScrollEndInterval = 250,
  children,
}) => {
  const ref = useRef<number>(0);

  const handleScrollEnd = useCallback(() => {
    const { clientHeight, innerHeight, scrollY } = getScrollValues();
    const timestamp = dayjs().unix();
    const scrollDiff = scrollY - ref.current;

    if (onScrollEnd) {
      onScrollEnd(clientHeight, innerHeight, scrollY, scrollDiff, timestamp);
    }
  }, [onScrollEnd]);

  const handleScroll = useCallback(() => {
    const { clientHeight, innerHeight, scrollY } = getScrollValues();
    const timestamp = dayjs().unix();
    const scrollDiff = scrollY - ref.current;
    ref.current = scrollY;

    if (onScroll)
      onScroll(clientHeight, innerHeight, scrollY, scrollDiff, timestamp);
  }, [onScroll]);

  const { handleEvent } = useEventEnd({
    eventName: "scroll",
    onEvent: handleScroll,
    onEventEnd: handleScrollEnd,
    handleEventEndInterval: handleScrollEndInterval,
  });

  const handleThrottleScroll = throttle(handleEvent, handleScrollInterval);

  useEffect(() => {
    window.addEventListener("scroll", handleThrottleScroll);
    return () => window.removeEventListener("scroll", handleThrottleScroll);
  }, [handleThrottleScroll]);

  return <>{children}</>;
};
